import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

interface Buyer {
  id: string;
  companyName: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  country: string;
  category: string;
  createdAt: string;
}

const categoryColors: Record<string, { bg: string; text: string; dot: string }> = {
  roaster: { bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
  importer: { bg: 'bg-purple-50', text: 'text-purple-700', dot: 'bg-purple-500' },
  trader: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  other: { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' },
};

export const BuyerList: React.FC = () => {
  const navigate = useNavigate();
  const [buyers, setBuyers] = useState<Buyer[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetch('/api/admin/buyers', { credentials: 'include' })
      .then(r => r.json())
      .then(data => setBuyers(Array.isArray(data) ? data : []))
      .catch(() => setBuyers([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = buyers.filter(b => {
    if (filter !== 'all' && b.category !== filter) return false;
    if (search) {
      const term = search.toLowerCase();
      return b.companyName.toLowerCase().includes(term) ||
        b.contactName?.toLowerCase().includes(term) ||
        b.country?.toLowerCase().includes(term) ||
        b.contactEmail?.toLowerCase().includes(term);
    }
    return true;
  });

  const categoryCounts = buyers.reduce((acc, b) => {
    acc[b.category] = (acc[b.category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Delete ${name}? This cannot be undone.`)) return;
    const res = await fetch('/api/admin/buyers', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ id }),
    });
    if (res.ok) setBuyers(prev => prev.filter(b => b.id !== id));
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A] tracking-tight">Buyers & Clients</h1>
          <p className="text-sm text-[#A3A3A3] mt-1">{buyers.length} total clients</p>
        </div>
        <Link
          to="/admin/buyers/new"
          className="px-5 py-2.5 bg-[#1A1A1A] text-white rounded-xl text-sm font-medium hover:bg-[#2a2a2a] active:scale-[0.98] transition-all shadow-sm shadow-black/10"
        >
          + Add Buyer
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {['roaster', 'importer', 'trader', 'other'].map(cat => {
          const c = categoryColors[cat];
          return (
            <div key={cat} className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-5 shadow-sm shadow-black/[0.03] hover:shadow-md hover:shadow-black/[0.05] transition-shadow">
              <div className="flex items-center gap-2 mb-2">
                <span className={`w-2 h-2 rounded-full ${c.dot}`} />
                <p className="text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">{cat}s</p>
              </div>
              <p className="text-2xl font-bold text-[#1A1A1A] tabular-nums">{categoryCounts[cat] || 0}</p>
            </div>
          );
        })}
      </div>

      {/* Search + Filters */}
      <div className="flex gap-3 mb-6">
        <div className="flex-1 relative">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#A3A3A3" strokeWidth="1.5" className="absolute left-3.5 top-1/2 -translate-y-1/2">
            <circle cx="7" cy="7" r="4.5"/><path d="M10.5 10.5L14 14" strokeLinecap="round"/>
          </svg>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search buyers..."
            className="w-full pl-10 pr-4 py-2.5 border border-[#E8E6E2] rounded-xl bg-[#FAFAF8] text-sm text-[#1A1A1A] placeholder-[#C4C4C4] focus:outline-none focus:ring-2 focus:ring-[#1A1A1A]/10 focus:border-[#1A1A1A]/30 focus:bg-white transition-all"
          />
        </div>
        <div className="flex gap-1 bg-[#F5F3F0] rounded-xl p-1">
          {['all', 'roaster', 'importer', 'trader', 'other'].map(cat => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-3.5 py-2 text-xs font-medium rounded-lg transition-all ${
                filter === cat ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#6B6B6B] hover:text-[#1A1A1A]'
              }`}
            >
              {cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-white rounded-2xl animate-pulse shadow-sm" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-[#A3A3A3]">
          {search ? 'No buyers match your search' : 'No buyers yet. Add your first client.'}
        </div>
      ) : (
        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl overflow-hidden shadow-sm shadow-black/[0.03]">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#E8E6E2]/60 bg-[#FAFAF8]/50">
                <th className="text-left px-5 py-3.5 text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Company</th>
                <th className="text-left px-5 py-3.5 text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Contact</th>
                <th className="text-left px-5 py-3.5 text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Country</th>
                <th className="text-left px-5 py-3.5 text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Category</th>
                <th className="text-right px-5 py-3.5 text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(buyer => {
                const c = categoryColors[buyer.category] || categoryColors.other;
                return (
                  <tr key={buyer.id} className="border-b border-[#E8E6E2]/60 hover:bg-[#FAFAF8]/50 transition-colors">
                    <td className="px-5 py-4">
                      <Link to={`/admin/buyers/${buyer.id}`} className="font-semibold text-[#1A1A1A] hover:text-[#F05A1E] transition-colors">
                        {buyer.companyName}
                      </Link>
                    </td>
                    <td className="px-5 py-4">
                      <p className="text-sm text-[#1A1A1A]">{buyer.contactName}</p>
                      <p className="text-xs text-[#A3A3A3]">{buyer.contactEmail}</p>
                    </td>
                    <td className="px-5 py-4 text-sm text-[#6B6B6B]">{buyer.country}</td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full ${c.bg} ${c.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
                        {buyer.category}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => navigate(`/admin/buyers/${buyer.id}`)} className="px-2.5 py-1.5 text-[12px] font-medium text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-[#F0EDE8] rounded-lg transition-colors">
                          Edit
                        </button>
                        <button onClick={() => handleDelete(buyer.id, buyer.companyName)} className="px-2.5 py-1.5 text-[12px] font-medium text-[#C4C4C4] hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
